import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import { Alert, AlertTitle, Box, Dialog, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalCard, { ModalRow } from "../../components/modal/ModalCard";
import TimesheetSearchBar from "../../components/searchbar/TimesheetSearchBar";
import CollapsibleTable, { IColumn, IRow } from "../../components/table/collapse/CollapsibleTable";
import Toolbar from "../../components/toolbar/Toolbar";
import ICompany from "../../models/company";
import { ERole } from "../../models/enums/role";
import { IScanDisplay } from "../../models/stat";
import auth from "../../services/auth/AuthService";
import companyService from "../../services/company/CompanyService";
import DateUtils from "../../utils/dateUtils";
import { getAcombaTimesheet } from "../../utils/download/downloadAcombaTimesheet";
import DownloadCollapsibleTable from "../../utils/download/downloadCollapsibleTable";
import useTimesheetScans, { EMPTY_CHARS } from "./useTimesheetScans";
import InlineTimestampCell from "../../components/table/collapse/InlineTimestampCell";
import { canEdit } from "../../utils/permissions";
import StackedOverrideCell from "../../components/table/collapse/StackedOverrideCell";
import { theme } from "../../styles/themes/mainTheme";

// const ADJUSTED_TIME_COLUMN_INDEX = 2;

const asStack = (original?: string, override?: string, overrideColor = theme.palette.primary.main): { text: string; color?: string; fontStyle?: "bold" | "normal" }[] | undefined =>
    (original || override)
        ? [{ text: original || "" }, ...(override ? [{ text: override, color: overrideColor, fontStyle: "bold" as const }] : [])]
        : undefined;

const TimesheetPage = () => {
    // const { start, setStart, end, setEnd, selectablePropertyGroups, selectedPropertyGroups, setSelectedPropertyGroups, inOutRows, inOutTotal, adjustedTotal, workedTotal, overridenAdjustedTotal, overridenInOutTotal, getTimesheets, selectableEmployees, selectedEmployees, setSelectedEmployees, getData, switchEmployeeMode, hasAdjustedTime } = useTimesheetScans();
    const { start, setStart, end, setEnd, selectablePropertyGroups, selectedPropertyGroups, setSelectedPropertyGroups, inOutRows, inOutTotal, workedTotal, overridenInOutTotal, getTimesheets, selectableEmployees, selectedEmployees, setSelectedEmployees, getData, switchEmployeeMode, updateManualOverride } = useTimesheetScans();
    const [formattedRows, setFormattedRows] = useState<IRow<IScanDisplay>[]>([]);
    const [isFilterActive, setIsFilterActive] = useState(true);
    const [selectedMode, setSelectedMode] = useState<"left" | "right">("left");
    const { t, i18n } = useTranslation();
    const [company, setCompany] = useState<ICompany>();
    const [acombaHasMissing, setAcombaHasMissing] = useState(false);
    const [acombaHasOverrides, setAcombaHasOverrides] = useState(false);
    const isLimitedClient = auth.user?.role === ERole.LimitedClient;
    const [expandedIds, setExpandedIds] = useState<Set<number | string>>(new Set());

    const toggleExpand = (id: number | string) =>
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    useEffect(() => {
        setExpandedIds(prev => {
            const visible = new Set(formattedRows.map(r => r.id));
            const next = new Set(Array.from(prev).filter(id => visible.has(id)));
            return next;
        });
    }, [inOutRows]);

    const columns: IColumn<IScanDisplay>[] = [
        { field: "username", headerName: t("header.employee") },
        {
            field: "minutes",
            headerName: `${t("header.totalStartEndTime")}\n(${DateUtils.displayMinutes(inOutTotal)})${overridenInOutTotal ? `\n(${DateUtils.displayMinutes(overridenInOutTotal)})` : ""}`,
            tooltipInfo: `${t(`info.totalStartEndTime`)}`,
            renderCustomHeader: () => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', }}>
                    <Box>{t("header.totalStartEndTime")}</Box>
                    <Box>({DateUtils.displayMinutes(inOutTotal)})</Box>
                    {overridenInOutTotal ? <Box style={{ color: theme.palette.primary.main }}>({DateUtils.displayMinutes(overridenInOutTotal)})</Box> : null}
                </Box>
            ),
            renderCustomCell: ({ row }) =>
                row.data ? (
                    <StackedOverrideCell
                        original={row.data.minutes}
                        override={row.data.overridenMinutes}
                    />
                ) : undefined,
            pdfRender: ({ row }) => asStack(row.data.minutes, row.data.overridenMinutes),
        },
        {
            field: "workedMinutes",
            headerName: `${t("header.totalWorkedTime")}\n(${DateUtils.displayMinutes(workedTotal)})`,
            tooltipInfo: `${t(`info.totalWorkedTime`)}`,
            renderCustomHeader: () => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', }}>
                    <Box>{t("header.totalWorkedTime")}</Box>
                    <Box>({DateUtils.displayMinutes(workedTotal)})</Box>
                </Box>
            ),
        },
    ];

    const subColumns: IColumn<IScanDisplay>[] = [
        {
            field: "date",
            headerName: t("header.startEndDate"),
            tooltipInfo: `${t(`info.startEndDate`)}`,
            renderCustomCell: ({ subrow }) => {
                if (!subrow) return undefined;

                return (
                    <InlineTimestampCell
                        userId={subrow.userId}
                        code={subrow.localCode}
                        originalStart={subrow.date}
                        originalEnd={subrow.endDate}
                        overridenStart={subrow.overridenStart}
                        overridenEnd={subrow.overridenEnd}
                        overridenStartAuthor={subrow.overridenStartAuthor}
                        overridenEndAuthor={subrow.overridenEndAuthor}
                        isEndAutoGenerated={subrow.isEndAutoGenerated}
                        isStartLate={subrow.isStartLate}
                        isEndEarly={subrow.isEndEarly}
                        canEdit={canEdit()}
                        onOverrideUIUpdate={(payload) => updateManualOverride({
                            userId: payload.userId,
                            inOutCode: payload.inOutCode,
                            originalTimestamp: payload.originalTimestamp,
                            overrideTimestamp: payload.overrideTimestamp,
                        })}
                    />
                );
            },
            pdfRender: ({ subrow }) => {
                if (!subrow) return [];

                const startDate = moment.utc(subrow.date).local().format("LLL");
                const overridenStart = subrow.overridenStart
                    ? moment.utc(subrow.overridenStart).local().format("LLL")
                    : null;

                const endDate = subrow.endDate
                    ? moment.utc(subrow.endDate).local().format("LLL")
                    : EMPTY_CHARS;
                const overridenEnd = subrow.overridenEnd
                    ? moment.utc(subrow.overridenEnd).local().format("LLL")
                    : null;

                return [
                    { text: startDate },
                    ...(overridenStart ? [{ text: overridenStart, color: theme.palette.primary.main, fontStyle: "bold" as const }] : []),
                    subrow.isEndAutoGenerated ? { text: endDate, color: theme.palette.error.main, fontStyle: "bold" as const } : { text: endDate },
                    ...(overridenEnd ? [{ text: overridenEnd, color: theme.palette.primary.main, fontStyle: "bold" as const }] : []),
                ];
            },
        },
        {
            field: "minutes",
            headerName: t("header.startEndTime"),
            tooltipInfo: `${t(`info.startEndTime`)}`,
            renderCustomCell: ({ subrow }) =>
                subrow ? (
                    <StackedOverrideCell
                        original={subrow.minutes}
                        override={subrow.overridenMinutes}
                    />
                ) : undefined,
            pdfRender: ({ subrow }) => (subrow ? asStack(subrow.minutes, subrow.overridenMinutes) : undefined),
        },
        {
            field: "workedMinutes",
            headerName: t("header.workedTime"),
            tooltipInfo: `${t(`info.workedTime`)}`,
        },
    ];

    // if (hasAdjustedTime) {
    //     columns.splice(ADJUSTED_TIME_COLUMN_INDEX, 0, {
    //         field: "adjustedMinutes",
    //         headerName: `${t("header.totalAdjustedTime")}\n(${DateUtils.displayMinutes(adjustedTotal)})${overridenAdjustedTotal ? `\n(${DateUtils.displayMinutes(overridenAdjustedTotal)})` : ""}`,
    //         tooltipInfo: `${t(`info.totalAdjustedTime`)}`,
    //         renderCustomCell: ({ row }) =>
    //             row.data ? (
    //                 <StackedOverrideCell
    //                     original={row.data.adjustedMinutes!}
    //                     override={row.data.overridenAdjustedMinutes}
    //                 />
    //             ) : undefined,
    //         pdfRender: ({ row }) => asStack(row.data.adjustedMinutes, row.data.overridenAdjustedMinutes),

    //     });

    //     subColumns.splice(ADJUSTED_TIME_COLUMN_INDEX, 0, {
    //         field: "adjustedMinutes",
    //         headerName: t("header.adjustedTime"),
    //         tooltipInfo: `${t(`info.adjustedTime`)}`,
    //         renderCustomCell: ({ subrow }) =>
    //             subrow ? (
    //                 <StackedOverrideCell
    //                     original={subrow.adjustedMinutes!}
    //                     override={subrow.overridenAdjustedMinutes}
    //                 />
    //             ) : undefined,
    //         pdfRender: ({ subrow }) => (subrow ? asStack(subrow.adjustedMinutes, subrow.overridenAdjustedMinutes) : undefined),
    //     });
    // }

    if (!isLimitedClient) {
        columns.push({
            field: "productivity",
            headerName: t("header.totalProductivity"),
            tooltipInfo: `${t(`info.totalProductivity`)}`,
            renderCustomCell: ({ row }) =>
                row && row.data.productivity ? (
                    <StackedOverrideCell
                        original={row.data.productivity}
                        override={row.data.overridenProductivity}
                    />
                ) : undefined,
            pdfRender: ({ row }) => asStack(row.data.productivity, row.data.overridenProductivity),

        });
        subColumns.push({
            field: "productivity",
            headerName: t("header.productivity"),
            tooltipInfo: `${t(`info.productivity`)}`,
            renderCustomCell: ({ subrow }) =>
                subrow && subrow.productivity ? (
                    <StackedOverrideCell
                        original={subrow.productivity}
                        override={subrow.overridenProductivity}
                    />
                ) : undefined,
            pdfRender: ({ subrow }) => (subrow ? asStack(subrow.productivity, subrow.overridenProductivity) : undefined),
        });
    }

    const downloadTableFilters = [
        `${t("header.from")} ${moment.utc(start).local().format("LLL")} ${t("header.to")} ${moment.utc(end).local().format("LLL")}`,
        `${t("header.mainFilter")}: ${selectedMode === "left" ? t("table.property.propertyGroup") : t("header.employee")}`,
        `${selectedMode === "right" ? selectedEmployees.map((id) => selectableEmployees.find((employee) => employee.id === id)?.name).join(", ") : ""}`,
        `${selectedPropertyGroups.map((id) => selectablePropertyGroups.find((group) => group.id === id)?.name).join(", ")}`,
        `${selectedMode === "left" ? selectedEmployees.map((id) => selectableEmployees.find((employee) => employee.id === id)?.name).join(", ") : ""}`,
    ]

    const downloadTable = new DownloadCollapsibleTable(t("data.timesheets"), formattedRows, columns, subColumns, downloadTableFilters.filter(Boolean));

    useEffect(() => {
        setFormattedRows(inOutRows);
    }, [inOutRows, i18n.language]);

    useEffect(() => {
        const isEmployeeMode = selectedMode === "right";
        getData(isEmployeeMode);
    }, [start, end]);

    useEffect(() => {
        const isEmployeeMode = selectedMode === "right";
        switchEmployeeMode(isEmployeeMode);
    }, [selectedMode]);

    useEffect(() => {
        const isEmployeeMode = selectedMode === "right";
        getTimesheets(isEmployeeMode);
        if (isEmployeeMode) return;

        setSelectedEmployees([])
    }, [selectedPropertyGroups, selectedMode])

    useEffect(() => {
        const isEmployeeMode = selectedMode === "right";
        getTimesheets(isEmployeeMode);
        if (!isEmployeeMode) return;

        setSelectedPropertyGroups([])
    }, [selectedEmployees, selectedMode])

    useEffect(() => {
        companyService.GetCompanyByUser().then(setCompany);
    }, []);

    return (
        <>
            <Toolbar isFilterActive={isFilterActive} onFilterClick={() => setIsFilterActive(!isFilterActive)}
                downloadButtons={[
                    { label: "PDF", onClick: () => downloadTable.GetPDF(t("header.exportedBy"), t("header.exportDate")) },
                    { label: "Acomba", onClick: async () => getAcombaTimesheet(formattedRows, company?.name ?? "", setAcombaHasMissing, setAcombaHasOverrides) }
                ]}
                segmentedSwitchProps={{
                    selected: selectedMode,
                    onChange: (value) => setSelectedMode(value),
                    leftIcon: <BusinessIcon />,
                    rightIcon: <PersonIcon />,
                    leftLabel: t("table.property.propertyGroup"),
                    rightLabel: t("header.employee"),
                    tooltipText: t("header.timesheetSwitchTooltip")

                }} />

            {isFilterActive && (
                <TimesheetSearchBar
                    propertyGroups={selectablePropertyGroups}
                    setSelectedPropertyGroups={setSelectedPropertyGroups}
                    selectedPropertyGroups={selectedPropertyGroups}
                    employees={selectableEmployees}
                    selectedEmployees={selectedEmployees}
                    setSelectedEmployees={setSelectedEmployees}
                    start={start}
                    setStart={setStart}
                    end={end}
                    setEnd={setEnd}
                    isEmployeeMode={selectedMode === "right"}
                />
            )}

            <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                <CollapsibleTable
                    rows={formattedRows}
                    columns={columns}
                    subColumns={subColumns}
                    expandedIds={expandedIds}
                    onToggleExpand={toggleExpand}
                />
            </Box>

            <Dialog fullWidth open={acombaHasMissing || acombaHasOverrides} onClose={() => { setAcombaHasMissing(false); setAcombaHasOverrides(false); }}>
                <ModalCard title={t('header.timesheetAcombaModalTitle')} closeCallback={() => { setAcombaHasMissing(false); setAcombaHasOverrides(false); }}>
                    <ModalCard.Main isDivider={false}>
                        <ModalRow>
                            <Box >
                                {acombaHasMissing && (
                                    <Alert severity="warning" sx={{ mb: acombaHasOverrides ? 2 : 0 }}>
                                        <AlertTitle>{t('header.timesheetAcombaMissingTitle')}</AlertTitle>
                                        <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                            {t("header.timesheetAcombaMissingMessage1")}
                                        </Typography>
                                        <Box component="ol" sx={{ pl: 3, mb: 0 }}>
                                            <li>
                                                <Typography variant="body2">
                                                    {t("header.timesheetAcombaMissingMessage2")}
                                                </Typography>
                                            </li>
                                            <br />
                                            <li>
                                                <Typography variant="body2">
                                                    {t("header.timesheetAcombaMissingMessage3")}
                                                </Typography>
                                            </li>
                                            <br />
                                            <li>
                                                <Typography variant="body2">
                                                    {t("header.timesheetAcombaMissingMessage4")}
                                                </Typography>
                                            </li>
                                        </Box>
                                    </Alert>
                                )}

                                {acombaHasOverrides && (
                                    <Alert severity="info">
                                        <AlertTitle>{t('header.timesheetAcombaOverridesTitle')}</AlertTitle>
                                        <Typography variant="body2" >
                                            {t('header.timesheetAcombaOverridesMessage')}

                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                        </ModalRow>
                    </ModalCard.Main>
                </ModalCard>
            </Dialog>
        </>
    );
};

export default TimesheetPage;
